import { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  DeployedStrategiesPage,
  DesktopDownloadBanner,
  PublishedCodeDrawer,
  PublishedParametersSheet,
  StrategyDeploymentTable,
  UndeployDialog,
} from "@/components"
import type {
  DeploymentGroup,
  ParameterGroups,
  ParameterItem,
  PublishedDeployment,
} from "@/components"
import { tokenize, type CodeHighlightToken } from "@/lib/shikiHighlighter"
import { ThemeContext } from "../providers/ThemeProvider"
import { useApp } from "../hooks/useApp.jsx"
import { paywallTrigger } from "../system/paywallTrigger.js"
import { botApi, type PublishResult } from "./api"

interface BotLiveProps {
  /** Bump to force a reload (e.g. after a publish from BotDetail). */
  refreshTrigger?: number
  /** Optional callback fired when the deployment list becomes empty after a delete. */
  onEmpty?: () => void
}

const STRATEGY_GROUP_FALLBACK_NAME = "Strategy"

function formatDeployedDate(input: string): string {
  if (!input) return ""
  const normalised = input.includes("T") ? input : input.replace(" ", "T")
  const date = new Date(normalised)
  if (Number.isNaN(date.getTime())) return input
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function shortMobileDate(dateStr: string) {
  return dateStr.replace(/\s\d{4},/, ",")
}

function formatLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
}

function formatParamValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "number") return String(value)
  if (Array.isArray(value)) return value.length === 0 ? "—" : value.join(", ")
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

function flattenParams(
  config: Record<string, unknown> | undefined,
  prefix = "",
): ParameterItem[] {
  if (!config) return []
  const items: ParameterItem[] = []
  for (const [key, value] of Object.entries(config)) {
    const label = prefix ? `${prefix} · ${formatLabel(key)}` : formatLabel(key)
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      items.push(
        ...flattenParams(value as Record<string, unknown>, label),
      )
    } else {
      items.push({ label, value: formatParamValue(value) })
    }
  }
  return items
}

function buildParameterGroups(
  configInput: PublishResult["configInput"] | undefined,
): ParameterGroups {
  if (!configInput) return {}
  const groups: ParameterGroups = {}
  const strategy = flattenParams(configInput.strategy_config)
  if (strategy.length > 0) groups["Strategy Parameters"] = strategy
  const risk = flattenParams(configInput.risk_management_config)
  if (risk.length > 0) groups["Risk Management"] = risk
  return groups
}

function publishToDeployment(p: PublishResult): PublishedDeployment {
  const symbol =
    (p.configInput?.strategy_config?.symbol as string | undefined) ?? "—"
  const timeframe =
    (p.configInput?.strategy_config?.timeframe as string | undefined) ?? "—"
  const date = formatDeployedDate(p.created)
  const ver = p.version != null ? `v${p.version}` : "—"
  return {
    id: p.pcid,
    stratId: p.botBid ?? p.pcid,
    name: p.botTitle ?? STRATEGY_GROUP_FALLBACK_NAME,
    ver,
    sym: symbol,
    tf: timeframe,
    date,
    publishedAt: Date.parse(p.created.includes("T") ? p.created : p.created.replace(" ", "T")) || 0,
    enabled: !!p.isConnected,
    note: p.title ?? "",
  }
}

function buildGroups(publishes: PublishResult[]): DeploymentGroup[] {
  const byStrat = new Map<string, PublishResult[]>()
  for (const p of publishes) {
    const key = p.botBid ?? p.pcid
    const list = byStrat.get(key) ?? []
    list.push(p)
    byStrat.set(key, list)
  }
  const groups: DeploymentGroup[] = []
  for (const [stratId, list] of byStrat) {
    const sorted = [...list].sort(
      (a, b) => Date.parse(b.created) - Date.parse(a.created),
    )
    const deployments = sorted.map(publishToDeployment)
    groups.push({
      stratId,
      latest: deployments[0],
      all: deployments,
      latestAt: deployments[0]?.publishedAt ?? 0,
    })
  }
  groups.sort((a, b) => b.latestAt - a.latestAt)
  return groups
}

export default function BotLive({ refreshTrigger = 0, onEmpty }: BotLiveProps = {}) {
  const navigate = useNavigate()
  const themeCtx = useContext(ThemeContext) as { theme?: string } | undefined
  const isDark = themeCtx?.theme === "dark"

  const app = useApp() as
    | { session?: { subscription?: { slug?: string } | null } | null }
    | null
  const isFreePlan = app?.session?.subscription?.slug === "free"

  const [publishes, setPublishes] = useState<PublishResult[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [paramsTarget, setParamsTarget] = useState<PublishResult | null>(null)
  const [codeTarget, setCodeTarget] = useState<PublishResult | null>(null)
  const [codeText, setCodeText] = useState<string>("")
  const [codeLoading, setCodeLoading] = useState(false)
  const [codeLines, setCodeLines] = useState<CodeHighlightToken[][] | undefined>(
    undefined,
  )
  const [undeployTarget, setUndeployTarget] = useState<PublishResult | null>(null)
  const [undeployGroup, setUndeployGroup] = useState<DeploymentGroup | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await botApi.getAllPublishedBots()
      setPublishes(data)
    } catch (err) {
      console.error("Error loading deployed strategies:", err)
      setError("Failed to load deployed strategies.")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load, refreshTrigger])

  // Lazy-load code text when the drawer opens
  useEffect(() => {
    if (!codeTarget) {
      setCodeText("")
      setCodeLines(undefined)
      return
    }
    let cancelled = false
    setCodeLoading(true)
    setCodeText("")
    setCodeLines(undefined)
    botApi
      .getCodeByCid(codeTarget.cid)
      .then((data) => {
        if (cancelled) return
        setCodeText(data?.code ?? "")
      })
      .catch((err) => {
        if (cancelled) return
        console.error("Error loading code:", err)
        setCodeText("")
      })
      .finally(() => {
        if (!cancelled) setCodeLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [codeTarget])

  useEffect(() => {
    if (!codeText) {
      setCodeLines(undefined)
      return
    }
    let cancelled = false
    tokenize(codeText, isDark ? "github-dark" : "github-light")
      .then((tokens) => {
        if (!cancelled) setCodeLines(tokens)
      })
      .catch(() => {
        if (!cancelled) setCodeLines(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [codeText, isDark])

  const groups = useMemo(() => buildGroups(publishes), [publishes])
  const totalVersions = publishes.length
  const summary =
    totalVersions > 0
      ? `${groups.length} ${groups.length === 1 ? "strategy" : "strategies"} · ${totalVersions} ${totalVersions === 1 ? "version" : "versions"}`
      : undefined

  const parameters = useMemo(
    () => buildParameterGroups(paramsTarget?.configInput),
    [paramsTarget],
  )

  const handleNoteChange = async (pcid: string, note: string) => {
    const previous = publishes
    setPublishes((prev) =>
      prev.map((p) => (p.pcid === pcid ? { ...p, title: note } : p)),
    )
    try {
      await botApi.renamePublishedBot(pcid, note)
    } catch (err) {
      console.error("Error renaming deployment:", err)
      setPublishes(previous)
    }
  }

  const handleDelete = async (pcid: string) => {
    try {
      await botApi.deletePublishedBot(pcid)
      setPublishes((prev) => {
        const next = prev.filter((p) => p.pcid !== pcid)
        if (next.length === 0) onEmpty?.()
        return next
      })
    } catch (err) {
      console.error("Error deleting deployment:", err)
      setError("Failed to delete deployment.")
    }
  }

  const handleDeleteGroup = async (group: DeploymentGroup) => {
    const pcids = group.all.map((entry) => String(entry.id))
    try {
      await Promise.all(pcids.map((pcid) => botApi.deletePublishedBot(pcid)))
      setPublishes((prev) => {
        const next = prev.filter((p) => !pcids.includes(p.pcid))
        if (next.length === 0) onEmpty?.()
        return next
      })
    } catch (err) {
      console.error("Error deleting deployment group:", err)
      setError("Failed to delete deployments.")
    }
  }

  const goToStrategy = (botBid: string | undefined) => {
    if (!botBid) return
    navigate(`/strategy/${botBid}?mode=builder`)
  }

  const publishById = (id: number | string): PublishResult | undefined =>
    publishes.find((p) => p.pcid === String(id))

  const confirmVersion = undeployTarget?.version != null ? `v${undeployTarget.version}` : ""
  const confirmGroupName = undeployGroup
    ? publishById(undeployGroup.latest.id)?.botTitle ?? STRATEGY_GROUP_FALLBACK_NAME
    : ""

  const codeSubtitle = codeTarget
    ? `${codeTarget.version != null ? `v${codeTarget.version} · ` : ""}${codeTarget.configInput?.strategy_config?.symbol ?? "—"} ${codeTarget.configInput?.strategy_config?.timeframe ?? ""} · ${formatDeployedDate(codeTarget.created)}`
    : undefined

  let bodyState: "loading" | "error" | "ready" = "ready"
  if (isLoading) bodyState = "loading"
  else if (error) bodyState = "error"

  return (
    <>
      <DeployedStrategiesPage
        heading="Deployed Strategies"
        description="Every strategy you deploy is saved here, ready to run. Open the AlgoBuilder desktop app to start trading them on MetaTrader 5."
        bannerSlot={
          <DesktopDownloadBanner
            title={
              isFreePlan
                ? "Upgrade to download the Windows app and start trading"
                : "Install the desktop app to start trading"
            }
            description="The desktop app runs your strategy code on MetaTrader 5 via its built-in Python bridge. Without it, your deployed strategies can't run."
            downloadLabel="Download for Windows"
            platformCaption="Windows 10/11 · 64-bit"
            upgradeMode={isFreePlan}
            upgradeLabel="Upgrade"
            onUpgrade={() => paywallTrigger.open()}
            onDownload={() => {
              const a = document.createElement("a")
              a.href = "/downloads/algobuilder-setup.exe"
              a.download = "algobuilder-setup.exe"
              document.body.appendChild(a)
              a.click()
              a.remove()
            }}
          />
        }
        sectionHeading="Deployments"
        summary={summary}
        isEmpty={bodyState === "ready" && groups.length === 0}
      >
        {bodyState === "loading" && (
          <div className="rounded-xl border border-border bg-card shadow-md p-10 flex items-center justify-center text-body text-muted-foreground">
            Loading deployed strategies…
          </div>
        )}

        {bodyState === "error" && (
          <div className="rounded-xl border border-danger bg-danger-dim p-4 text-body text-danger">
            {error}
          </div>
        )}

        {bodyState === "ready" &&
          groups.map((group) => {
            const groupPublish = publishById(group.latest.id)
            const strategyName =
              groupPublish?.botTitle ?? STRATEGY_GROUP_FALLBACK_NAME
            return (
              <StrategyDeploymentTable
                key={group.stratId}
                group={group}
                strategyName={strategyName}
                isEntryRunning={() => false}
                onOpenCode={(entry) => {
                  const target = publishById(entry.id)
                  if (target) setCodeTarget(target)
                }}
                onOpenParams={(entry) => {
                  const target = publishById(entry.id)
                  if (target) setParamsTarget(target)
                }}
                onBuild={(entry) => {
                  const target = publishById(entry.id)
                  goToStrategy(target?.botBid)
                }}
                onUndeployVersion={(entry) => {
                  const target = publishById(entry.id)
                  if (target) setUndeployTarget(target)
                }}
                onUndeployAll={() => setUndeployGroup(group)}
                onNoteChange={(entry, note) =>
                  handleNoteChange(String(entry.id), note)
                }
                formatMobileDate={shortMobileDate}
                labels={{ notes: "Note" }}
              />
            )
          })}
      </DeployedStrategiesPage>

      <PublishedParametersSheet
        open={paramsTarget !== null}
        onOpenChange={(open) => {
          if (!open) setParamsTarget(null)
        }}
        parameters={parameters}
      />

      <PublishedCodeDrawer
        open={codeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCodeTarget(null)
        }}
        code={codeText}
        title={codeTarget?.title || "Code"}
        subtitle={codeSubtitle}
        tokenizedLines={codeLines}
        bannerSlot={
          codeLoading ? (
            <div className="px-4 sm:px-5 py-2 text-caption text-muted-foreground border-b border-border">
              Loading code…
            </div>
          ) : undefined
        }
      />

      <UndeployDialog
        open={undeployTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUndeployTarget(null)
        }}
        mode="version"
        title={`Delete ${confirmVersion || "version"}?`}
        description={
          <>
            This version will be removed from your desktop app on its next sync
            and cannot be recovered. Type the version to confirm:{" "}
            <span className="font-mono font-semibold text-foreground">
              {confirmVersion}
            </span>
          </>
        }
        confirmValue={confirmVersion}
        onConfirm={() => {
          if (undeployTarget) handleDelete(undeployTarget.pcid)
          setUndeployTarget(null)
        }}
      />

      <UndeployDialog
        open={undeployGroup !== null}
        onOpenChange={(open) => {
          if (!open) setUndeployGroup(null)
        }}
        mode="group"
        title={`Delete all ${undeployGroup?.all.length ?? 0} versions?`}
        description={
          <>
            Every deployed version of this strategy will be removed from your
            desktop app and cannot be recovered. Type the strategy name to
            confirm:{" "}
            <span className="font-semibold text-foreground">{confirmGroupName}</span>
          </>
        }
        confirmValue={confirmGroupName}
        onConfirm={() => {
          if (undeployGroup) handleDeleteGroup(undeployGroup)
          setUndeployGroup(null)
        }}
      />
    </>
  )
}
